import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { useEffect, useId, useRef } from 'react';
import type { LucideIcon } from 'lucide-react';
import { AlertTriangle, Loader2, X } from 'lucide-react';
import { ModuleHero } from './ModuleHero';
import { useOverlayScrollLock } from '../hooks/useOverlayScrollLock';

type Tone = 'ok' | 'warn' | 'info' | 'accent';

interface PageHeroProps {
  eyebrow?: string;
  title: string;
  description?: string;
  icon: LucideIcon;
  accent?: 'arc' | 'ice' | 'goal';
  actions?: ReactNode;
  status?: { label: string; tone?: Exclude<Tone, 'err'> };
}

export function PageHero(props: PageHeroProps) {
  return <ModuleHero {...props} />;
}

export function AdminModulePage({ children }: { children: ReactNode }) {
  return <div className="page-template page-template--admin">{children}</div>;
}

export function EventWorkspaceSection({ children }: { children: ReactNode }) {
  return <div className="page-template page-template--workspace">{children}</div>;
}

export function PublicEventSection({ children }: { children: ReactNode }) {
  return <div className="page-template page-template--public">{children}</div>;
}

interface LoadingButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  loading?: boolean;
  loadingLabel?: string;
  variant?: 'primary' | 'ghost' | 'danger';
  size?: 'sm' | 'md';
}

export function LoadingButton({
  children,
  className = '',
  disabled,
  loading,
  loadingLabel,
  size = 'md',
  type = 'button',
  variant = 'primary',
  ...props
}: LoadingButtonProps) {
  const variantClass = variant === 'primary' ? 'btn--primary' : variant === 'danger' ? 'btn--danger' : 'btn--ghost';
  return (
    <button
      {...props}
      type={type}
      className={`btn ${variantClass} ${size === 'sm' ? 'btn--sm' : ''} loading-button ${className}`.trim()}
      disabled={disabled || loading}
      aria-busy={loading ? 'true' : undefined}
    >
      {loading && <Loader2 size={14} className="spin" aria-hidden />}
      {loading ? loadingLabel ?? children : children}
    </button>
  );
}

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  body: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'warn' | 'danger';
  onCancel: () => void;
  onConfirm: () => void;
}

export function ConfirmDialog({
  body,
  cancelLabel = 'Cancel',
  confirmLabel = 'Confirm',
  onCancel,
  onConfirm,
  open,
  title,
  tone = 'warn',
}: ConfirmDialogProps) {
  useOverlayScrollLock(open);
  const titleId = useId();
  const panelRef = useTemplateOverlay<HTMLDivElement>(open, onCancel);
  if (!open) return null;
  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby={titleId} onClick={(event) => event.target === event.currentTarget && onCancel()}>
      <div className="modal overlay-panel confirm-dialog" ref={panelRef} tabIndex={-1}>
        <div className="modal__head overlay-head">
          <div className="confirm-dialog__title-row">
            <span className={`confirm-dialog__icon confirm-dialog__icon--${tone}`} aria-hidden>
              <AlertTriangle size={18} />
            </span>
            <div>
              <h2 className="modal__title" id={titleId}>{title}</h2>
              <div className="modal__sub">{body}</div>
            </div>
          </div>
          <button className="btn btn--ghost btn--sm modal__close" type="button" onClick={onCancel} aria-label="Close">
            <X size={16} />
          </button>
        </div>
        <div className="modal__footer overlay-footer">
          <button type="button" className="btn btn--ghost" onClick={onCancel}>{cancelLabel}</button>
          <button type="button" className={`btn ${tone === 'danger' ? 'btn--danger' : 'btn--primary'}`} onClick={onConfirm}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

interface InputConfirmDialogProps {
  open: boolean;
  title: string;
  body: ReactNode;
  label: string;
  value: string;
  placeholder?: string;
  confirmLabel?: string;
  onValueChange: (value: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
}

export function InputConfirmDialog({
  body,
  confirmLabel = 'Confirm',
  label,
  onCancel,
  onConfirm,
  onValueChange,
  open,
  placeholder,
  title,
  value,
}: InputConfirmDialogProps) {
  useOverlayScrollLock(open);
  const titleId = useId();
  const panelRef = useTemplateOverlay<HTMLDivElement>(open, onCancel);
  if (!open) return null;
  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby={titleId} onClick={(event) => event.target === event.currentTarget && onCancel()}>
      <div className="modal overlay-panel confirm-dialog" ref={panelRef} tabIndex={-1}>
        <div className="modal__head overlay-head">
          <div>
            <h2 className="modal__title" id={titleId}>{title}</h2>
            <p className="modal__sub">{body}</p>
          </div>
          <button className="btn btn--ghost btn--sm modal__close" type="button" onClick={onCancel} aria-label="Close">
            <X size={16} />
          </button>
        </div>
        <div className="modal__body overlay-body touch-scroll">
          <label className="field">
            <span className="field__label">{label}</span>
            <input className="input" value={value} placeholder={placeholder} onChange={(event) => onValueChange(event.target.value)} autoFocus />
          </label>
        </div>
        <div className="modal__footer overlay-footer">
          <button type="button" className="btn btn--ghost" onClick={onCancel}>Cancel</button>
          <button type="button" className="btn btn--primary" onClick={onConfirm} disabled={!value.trim()}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

export function SaveFooter({ children }: { children: ReactNode }) {
  return <div className="save-footer">{children}</div>;
}

function useTemplateOverlay<T extends HTMLElement>(open: boolean, onClose: () => void) {
  const panelRef = useRef<T | null>(null);
  useEffect(() => {
    if (!open) return undefined;
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    window.setTimeout(() => {
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = getFocusable(panel);
      (focusable[0] ?? panel).focus();
    }, 0);
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = getFocusable(panel);
      if (focusable.length === 0) {
        event.preventDefault();
        panel.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      previouslyFocused?.focus();
    };
  }, [onClose, open]);
  return panelRef;
}

function getFocusable(root: HTMLElement): HTMLElement[] {
  return Array.from(
    root.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ).filter((element) => !element.hasAttribute('aria-hidden') && element.offsetParent !== null);
}
